import React, { useState, useEffect } from 'react';
import { useData } from '../../context/DataContext';
import { mappingApi } from '../../services/api';

const MappedDataPage = () => {
  const { mappings, companies, farmers, deleteMapping, setMappings } = useData();
  const [companyFilter, setCompanyFilter] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchMappings();
  }, []);

  const fetchMappings = async () => {
    try {
      setLoading(true);
      console.log('Fetching all mappings...');
      const response = await mappingApi.getAllMappings();
      console.log('Raw mappings response:', response);
      
      // Handle different response formats
      let mappingsData = [];
      if (Array.isArray(response)) {
        mappingsData = response;
      } else if (response && response.results && Array.isArray(response.results)) {
        mappingsData = response.results;
      } else if (response && typeof response === 'object') {
        // Try to find an array in the response
        for (const key in response) {
          if (Array.isArray(response[key])) {
            mappingsData = response[key];
            break;
          }
        }
      }
      
      console.log('Processed mappings data:', mappingsData);
      setMappings(mappingsData);
    } catch (error) {
      console.error('Error fetching mappings:', error);
      setError('Failed to fetch mappings. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Get company and farmer details for each mapping
  const mappedData = mappings.map(mapping => {
    console.log('Processing mapping:', mapping);

    const companyId = typeof mapping.company === 'object' ? mapping.company.id : mapping.company;
    const farmerId = typeof mapping.farmer === 'object' ? mapping.farmer.id : mapping.farmer;

    const company = companies.find(c => c.id === companyId) || {};
    console.log('Found company:', company);

    // Try multiple ways to find the farmer
    let farmer = null;

    // Method 1: Direct ID match
    farmer = farmers.find(f => f.id === farmerId);

    // Method 2: String comparison of IDs
    if (!farmer) {
      farmer = farmers.find(f => String(f.id) === String(farmerId));
    }

    // Method 3: Try to find by numeric part of ID
    if (!farmer && typeof farmerId === 'string' && farmerId.match(/\d+/)) {
      const numericId = farmerId.match(/\d+/)[0];
      farmer = farmers.find(f => String(f.id).includes(numericId));
    }

    console.log('Found farmer:', farmer);

    // Get the real farmer name and location from the API data
    let farmerName = 'Unknown Farmer';
    let farmerLocation = 'Unknown Location';

    if (farmer && Object.keys(farmer).length > 0) {
      farmerName = farmer.farmer_name || farmer.name || 'Unknown Farmer';
      farmerLocation = farmer.village || farmer.district || farmer.location || 'Unknown Location';
    }

    const result = {
      ...mapping,
      companyId: companyId,
      companyName: company.name || 'Unknown Company',
      farmerName: farmerName,
      farmerLocation: farmerLocation,
      created_at: mapping.created_at || mapping.mappedDate // Use created_at if available, fallback to mappedDate
    };

    console.log('Mapped data result:', result);
    return result;
  });

  // Filter mapped data based on company filter and search term
  const filteredData = mappedData.filter(item => {
    const matchesCompany = companyFilter === '' || 
      (item.companyId && String(item.companyId) === companyFilter);
    const matchesSearch = searchTerm === '' || 
      (item.companyName && item.companyName.toLowerCase().includes(searchTerm.toLowerCase())) ||
      (item.farmerName && item.farmerName.toLowerCase().includes(searchTerm.toLowerCase()));

    return matchesCompany && matchesSearch;
  });

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this mapping?')) {
      try {
        await deleteMapping(id);
        // Refresh the mappings list
        await fetchMappings();
      } catch (error) {
        console.error('Error deleting mapping:', error);
        setError('Failed to delete mapping. Please try again.');
      }
    }
  };

  if (loading) {
    return <div style={styles.loading}>Loading...</div>;
  }

  if (error) {
    return <div style={styles.error}>{error}</div>;
  }

  return (
    <div style={styles.container}>
      <div style={styles.pageHeader}>
        <div style={styles.titleContainer}>
          <span style={styles.titleIcon}>🔗</span>
          <h1 style={styles.title}>Mapped Data</h1>
        </div>
        <p style={styles.subtitle}>
          View and manage farmer-company connections
        </p>
      </div>

      {mappings.length === 0 ? (
        <div style={styles.noMappingsContainer}>
          <div style={styles.noMappingsIcon}>🔍</div>
          <h2 style={styles.noMappingsTitle}>No Mappings Found</h2>
          <p style={styles.noMappingsText}>
            There are currently no farmer-company mappings in the system.
          </p>
          <a href="/admin/mapping" style={styles.createMappingButton}>
            Create New Mapping
          </a>
        </div>
      ) : (
        <>
          <div style={styles.filterSection}>
            <div style={styles.filterHeader}>
              <div style={styles.filterTitle}>
                <span style={styles.filterIcon}>🔍</span>
                <span>Search & Filter</span>
              </div>
              <div style={styles.resultsCount}>
                {filteredData.length} {filteredData.length === 1 ? 'mapping' : 'mappings'} found
              </div>
            </div>

            <div style={styles.filters}>
              <div style={styles.searchBox}>
                <input
                  type="text"
                  placeholder="Search by company or farmer name..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  style={styles.searchInput}
                />
                {searchTerm && (
                  <button
                    onClick={() => setSearchTerm('')}
                    style={styles.clearButton}
                    title="Clear search"
                  >
                    ✕
                  </button>
                )}
              </div>

              <div style={styles.filterBox}>
                <select
                  value={companyFilter}
                  onChange={(e) => setCompanyFilter(e.target.value)}
                  style={styles.filterSelect}
                >
                  <option value="">All Companies</option>
                  {companies.map(company => (
                    <option key={company.id} value={company.id}>
                      {company.name}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </div>

          <div style={styles.tableContainer}>
            <table style={styles.table}>
              <thead>
                <tr>
                  <th style={styles.tableHeader}>Serial</th>
                  <th style={styles.tableHeader}>Company</th>
                  <th style={styles.tableHeader}>Farmer</th>
                  <th style={styles.tableHeader}>Location</th>
                  <th style={styles.tableHeader}>Mapped Date</th>
                  <th style={styles.tableHeader}>Status</th>
                  <th style={styles.tableHeader}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredData.length > 0 ? (
                  filteredData.map((item, index) => (
                    <tr key={item.id} style={styles.tableRow}>
                      <td style={styles.tableCell}>{index + 1}</td>
                      <td style={styles.tableCell}>
                        <div style={styles.companyName}>{item.companyName}</div>
                      </td>
                      <td style={styles.tableCell}>
                        <div style={styles.farmerName}>{item.farmerName}</div>
                      </td>
                      <td style={styles.tableCell}>
                        <div style={styles.locationBadge}>{item.farmerLocation}</div>
                      </td>
                      <td style={styles.tableCell}>
                        {item.created_at ? new Date(item.created_at).toLocaleDateString('en-US', {
                          year: 'numeric',
                          month: 'short',
                          day: 'numeric'
                        }) : 'N/A'}
                      </td>
                      <td style={styles.tableCell}>
                        <span style={{
                          ...styles.statusBadge,
                          backgroundColor: item.status === 'active' ? 'var(--success-color)' : 'var(--danger-color)'
                        }}>
                          {item.status}
                        </span>
                      </td>
                      <td style={styles.tableCell}>
                        <button
                          onClick={() => handleDelete(item.id)}
                          style={styles.deleteButton}
                          title="Delete Mapping"
                        >
                          <span style={styles.deleteIcon}>🗑️</span>
                        </button>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="7" style={styles.noResults}>
                      <div style={styles.noResultsIcon}>🔍</div>
                      <div style={styles.noResultsText}>No mapped data found matching your criteria.</div>
                      <button
                        onClick={() => {
                          setSearchTerm('');
                          setCompanyFilter('');
                        }}
                        style={styles.resetButton}
                      >
                        Reset Filters
                      </button>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
};

const styles = {
  container: {
    maxWidth: '1200px',
    margin: '0 auto',
  },
  pageHeader: {
    marginBottom: '1.5rem',
    borderBottom: '1px solid var(--border-color)',
    paddingBottom: '1rem',
  },
  titleContainer: {
    display: 'flex',
    alignItems: 'center',
  },
  titleIcon: {
    fontSize: '2rem',
    marginRight: '0.75rem',
    color: 'var(--primary-color)',
  },
  title: {
    margin: 0,
    color: 'var(--text-color)',
    fontSize: '2rem',
    fontWeight: 'bold',
  },
  subtitle: {
    margin: '0.5rem 0 0 0',
    color: 'var(--text-light)',
    fontSize: '1rem',
  },
  filterSection: {
    backgroundColor: 'white',
    borderRadius: '12px',
    padding: '1.5rem',
    marginBottom: '1.5rem',
    boxShadow: 'var(--card-shadow)',
  },
  filterHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '1rem',
  },
  filterTitle: {
    fontSize: '1.1rem',
    fontWeight: 'bold',
    color: 'var(--text-color)',
    display: 'flex',
    alignItems: 'center',
  },
  filterIcon: {
    marginRight: '0.5rem',
  },
  resultsCount: {
    fontSize: '0.9rem',
    color: 'var(--text-light)',
    backgroundColor: 'var(--background-color)',
    padding: '0.4rem 0.8rem',
    borderRadius: '20px',
  },
  filters: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '1rem',
  },
  searchBox: {
    flex: '1 1 300px',
    position: 'relative',
  },
  searchInput: {
    width: '100%',
    padding: '0.85rem 1rem',
    borderRadius: '8px',
    border: '1px solid var(--border-color)',
    fontSize: '1rem',
    backgroundColor: 'rgba(255, 255, 255, 0.9)',
    paddingLeft: '2.5rem',
    backgroundImage: 'url("data:image/svg+xml,%3Csvg xmlns=\'http://www.w3.org/2000/svg\' width=\'16\' height=\'16\' viewBox=\'0 0 24 24\' fill=\'none\' stroke=\'%23999\' stroke-width=\'2\' stroke-linecap=\'round\' stroke-linejoin=\'round\'%3E%3Ccircle cx=\'11\' cy=\'11\' r=\'8\'%3E%3C/circle%3E%3Cline x1=\'21\' y1=\'21\' x2=\'16.65\' y2=\'16.65\'%3E%3C/line%3E%3C/svg%3E")',
    backgroundRepeat: 'no-repeat',
    backgroundPosition: '12px center',
    backgroundSize: '16px',
  },
  clearButton: {
    position: 'absolute',
    right: '12px',
    top: '50%',
    transform: 'translateY(-50%)',
    background: 'none',
    border: 'none',
    color: 'var(--text-light)',
    cursor: 'pointer',
    fontSize: '0.9rem',
  },
  filterBox: {
    flex: '0 0 300px',
    display: 'flex',
    flexDirection: 'column',
  },
  filterLabel: {
    fontSize: '0.9rem',
    marginBottom: '0.1rem',
    color: 'var(--text-light)',
  },
  filterSelect: {
    width: '100%',
    padding: '0.7rem 0.5rem',
    borderRadius: '8px',
    border: '1px solid var(--border-color)',
    fontSize: '0.9rem',
    backgroundColor: 'rgba(255, 255, 255, 0.9)',
    height: '46px',
  },
  tableContainer: {
    backgroundColor: 'white',
    borderRadius: '12px',
    padding: '1.5rem',
    boxShadow: 'var(--card-shadow)',
    overflowX: 'auto',
  },
  table: {
    width: '100%',
    borderCollapse: 'separate',
    borderSpacing: '0',
  },
  tableHeader: {
    textAlign: 'left',
    padding: '1rem',
    backgroundColor: 'var(--primary-light)',
    color: 'white',
    fontWeight: '600',
    fontSize: '1rem',
    whiteSpace: 'nowrap',
  },
  tableRow: {
    transition: 'background-color 0.2s',
    borderBottom: '1px solid var(--border-color)',
  },
  tableCell: {
    padding: '1rem',
    borderBottom: '1px solid var(--border-color)',
    fontSize: '0.95rem',
  },
  companyName: {
    fontWeight: '600',
    color: 'var(--primary-color)',
  },
  farmerName: {
    fontWeight: '600',
  },
  locationBadge: {
    display: 'inline-block',
    padding: '0.25rem 0.5rem',
    backgroundColor: 'var(--background-color)',
    borderRadius: '4px',
    fontSize: '0.85rem',
    color: 'var(--text-light)',
  },
  statusBadge: {
    display: 'inline-block',
    padding: '0.25rem 0.75rem',
    borderRadius: '20px',
    color: 'white',
    fontSize: '0.85rem',
    fontWeight: '500',
  },
  deleteButton: {
    padding: '0.5rem',
    backgroundColor: 'rgba(231, 76, 60, 0.1)',
    border: 'none',
    borderRadius: '50%',
    cursor: 'pointer',
    fontSize: '1rem',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    transition: 'all 0.2s',
  },
  deleteIcon: {
    fontSize: '1rem',
  },
  noResults: {
    padding: '3rem 1rem',
    textAlign: 'center',
    color: 'var(--text-light)',
  },
  noResultsIcon: {
    fontSize: '3rem',
    marginBottom: '1rem',
    opacity: '0.5',
  },
  noResultsText: {
    marginBottom: '1rem',
    fontSize: '1.1rem',
  },
  resetButton: {
    padding: '0.5rem 1rem',
    backgroundColor: 'var(--primary-color)',
    color: 'white',
    border: 'none',
    borderRadius: '4px',
    cursor: 'pointer',
    fontWeight: '500',
    display: 'inline-block',
    margin: '0 auto',
  },
  loading: {
    padding: '2rem',
    textAlign: 'center',
    color: 'var(--text-light)',
    fontSize: '1rem',
  },
  error: {
    padding: '2rem',
    textAlign: 'center',
    color: 'var(--danger-color)',
    fontSize: '1rem',
  },
  noMappingsContainer: {
    backgroundColor: 'white',
    borderRadius: '12px',
    padding: '3rem',
    boxShadow: 'var(--card-shadow)',
    textAlign: 'center',
    marginTop: '2rem',
  },
  noMappingsIcon: {
    fontSize: '4rem',
    marginBottom: '1.5rem',
    opacity: '0.7',
  },
  noMappingsTitle: {
    fontSize: '1.8rem',
    fontWeight: 'bold',
    marginBottom: '1rem',
    color: 'var(--text-color)',
  },
  noMappingsText: {
    fontSize: '1.1rem',
    color: 'var(--text-light)',
    marginBottom: '1.5rem',
    maxWidth: '500px',
    margin: '0 auto 1.5rem auto',
  },
  createMappingButton: {
    display: 'inline-block',
    padding: '0.75rem 1.5rem',
    backgroundColor: 'var(--primary-color)',
    color: 'white',
    textDecoration: 'none',
    borderRadius: '8px',
    fontWeight: '600',
    transition: 'background-color 0.2s',
  },
};

export default MappedDataPage;
